import styled from 'styled-components';
import React, {FC} from 'react';

import {
  fontSizesMixins,
  palette,
  mediaQueryMixins,
  menuBreakpoint,
  transition,
  paletteAlphas
} from '../../../../lib/styleVariables';
import {useFlag} from '../../../../lib/hooks/useFlag';
import {sendEvent} from '../../../../lib/front';
import {Icon} from '../../../molecules/icon';
import {HeaderMenuLeftItemWithSubMenuProps, HeaderMenuLeftSubItemProps} from './headerMenuTypes';
import {convertHexToRGBA} from '../../../../lib/color-utils';

export interface HeaderSubMenuCssProps {
  wrapperCss?: string;
}

interface HeaderSubMenuProps extends HeaderMenuLeftItemWithSubMenuProps, HeaderSubMenuCssProps {
  isOpen?: boolean;
  locationPath: string;
}

export const StyledSubMenuWrapper = styled.div<{$css?: string}>`
  cursor: default;
  min-width: 304px;

  position: absolute;
  left: 0;
  bottom: 0;
  padding-top: 8px;

  visibility: hidden;
  transform: translateY(100%);
  z-index: 1;
  opacity: 0;
  pointer-events: none;
  transition: ${transition.fast};

  :after {
    content: '';
    position: absolute;
    border-style: solid;
    border-color: transparent transparent ${palette.white.highlight} transparent;
    left: 48px;
    top: 0;
    border-width: 0px 10px 10px;
  }
  ${props => props.$css}
`;

const StyledSubMenuList = styled.div`
  background: ${palette.white.highlight};
  box-shadow: 0px 5px 26px rgba(13, 12, 63, 0.05), 0px 2px 10px ${paletteAlphas.black5};
  border-radius: 10px;
  padding: 12px 8px 8px;
`;

export const StyledSubMenuListItem = styled.a<{isLinkActive?: boolean}>`
  text-decoration: none;
  padding: 8px;
  display: flex;
  flex-basis: 50%;
  border-radius: 8px;
  transition: ${transition.fast};

  ${fontSizesMixins.text3()};
  color: ${palette.black.base};
  --menu-item-icon-color: ${props =>
    (props.isLinkActive ? convertHexToRGBA(palette.black.base, 0.4) : palette.blue.base)};
  background: ${props => (props.isLinkActive ? palette.white.base : 'transparent')};
  cursor: ${props => (props.isLinkActive ? 'default' : 'pointer')};
  pointer-events: ${props => (props.isLinkActive ? 'none' : 'visible')};

  ${mediaQueryMixins.minWidth(`${menuBreakpoint + 1}px`)} {
    &:hover {
      background: ${palette.white.base};
    }
  }

  ${mediaQueryMixins.maxWidth(`${menuBreakpoint}px`)} {
    background: transparent;
    &:hover {
      background: transparent;
    }
    [data-theme='dark'] & {
      color: ${palette.white.highlight};
    }
  }
`;

export const StyledSubMenuListItemIconWrapper = styled.span`
  margin-right: 16px;
  flex-shrink: 0;

  color: var(--menu-item-icon-color);
`;

export const HeaderSubMenu: FC<HeaderSubMenuProps> = props => {
  const [shouldShowAnalyzeInbox] = useFlag('pre-trial-inbox-analysis-switch', {
    defaultValue: false,
    timeout: 800
  });

  return (
    <StyledSubMenuWrapper $css={props.wrapperCss}>
      <StyledSubMenuList>
        {props.subMenu.items.map(subItem => {
          const subItemProps = computeSubItemProps(subItem, shouldShowAnalyzeInbox);

          return (
            <StyledSubMenuListItem
              href={subItemProps.href}
              isLinkActive={props.locationPath === subItem.href}
              key={`${subItemProps.name}-${subItemProps.href}`}
            >
              <StyledSubMenuListItemIconWrapper>
                <Icon name={subItemProps.iconName} size={24} />
              </StyledSubMenuListItemIconWrapper>
              <span>{subItemProps.name}</span>
            </StyledSubMenuListItem>
          );
        })}
      </StyledSubMenuList>
    </StyledSubMenuWrapper>
  );
};

/**
 * Returns different props depending on whether the feature flag is on. The
 * replacement props are defined in Contentful on the subitem itself.
 *
 * Currently only useful for turning Request ROI into Analyze Inbox.
 */
function computeSubItemProps(subItem: HeaderMenuLeftSubItemProps, isFlagOn: boolean) {
  if (
    isFlagOn &&
    subItem.featureFlag?.name === 'pre-trial-inbox-analysis-switch' &&
    subItem.featureFlag?.action === 'replace'
  )
    return {
      ...subItem.featureFlag.data,
      onClick: () => sendEvent('inbox-analysis-header-submenu-click')
    };

  return subItem;
}
